import React, { useEffect, useRef, useCallback, useState } from 'react';
import { SidemenuItem } from './SidemenuItem';
import { getService } from '@Client/runner.hooks/useService';
import { IMenuItem } from './sidemenu.service';

type SidemenuProps = {
  isMobile?: boolean;
};

export const Sidemenu = (props: SidemenuProps) => {
  const { isMobile } = props;
  const sideMenuService = getService('sideMenuService');
  const appConfig = getService('APP_CONFIG');
  const menuRef = useRef<HTMLElement>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const permissionsService = getService('permissionsService');

  const isPermitted = (item: IMenuItem) => {
    if (!item.permissions?.length) {
      return true;
    }

    return (
      permissionsService.currentUserHasAnyOfPermissions(item.permissions) ??
      false
    );
  };
  const menuItems = (sideMenuService?.menu || []).filter(isPermitted);
  const iconClosedSrc = appConfig?.runnerClosedLogoUrl;

  const closeMobileMenu = useCallback(() => setIsMobileOpen(false), []);

  useEffect(() => {
    if (!isMobile) return;

    const handleMenuToggle = (event: Event) => {
      event.preventDefault();
      setIsMobileOpen((prev) => !prev);
    };

    const menuToggle = document.querySelector('#header-side-menu-toggle');
    menuToggle?.addEventListener('click', handleMenuToggle);

    return () => menuToggle?.removeEventListener('click', handleMenuToggle);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sidebar = menuRef.current;
      const menuToggle = document.querySelector('#header-side-menu-toggle');

      if (
        sidebar &&
        !sidebar.contains(target) &&
        menuToggle &&
        !menuToggle.contains(target)
      ) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, isMobileOpen]);

  return (
    <aside
      className={`${isMobileOpen ? 'menu-open' : 'menu-closed'}`}
      data-testid="sidebar"
      role="navigation"
      aria-label="Side menu"
      ref={menuRef}
    >
      <div id="side-menu-header">
        {!isMobile && (
          <a className="brand-logo-desktop flex flex-row mt-10">
            <div
              className="brand-logo-image"
              style={{
                backgroundImage: `url('${iconClosedSrc}')`,
                width: `${appConfig.brandingLogoWidth}px`,
                height: `${appConfig.brandingLogoHeight}px`
              }}
            ></div>
          </a>
        )}

        {isMobile && (
          <>
            <div
              id="sidemenu-mobile-toggle"
              data-testid="sidemenu-mobile-toggle"
              onClick={() => setIsMobileOpen((prev) => !prev)}
            >
              <i className="fa-light fa-xmark"></i>
            </div>
            <div className="brand-logo">
              <img src={iconClosedSrc} alt="Logo" />
            </div>
          </>
        )}
      </div>
      <div className="side-menu__items">
        {menuItems.map((item) => (
          <SidemenuItem
            key={item.name}
            item={item}
            isMobile={isMobile}
            onMobileClose={closeMobileMenu}
          />
        ))}
      </div>
    </aside>
  );
};
